import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	CalendarDaysIcon,
	ClockIcon,
	CurrencyDollarIcon,
	ExclamationTriangleIcon,
	UserGroupIcon,
	DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { Modal, Spinner, Alert } from '../../components/ui';
import { staffProfileApi, ApiError } from '../../api';
import type { StaffBookingDetail } from '../../types/staffBookings';

// Task badge color mapping (shared with Bookings page)
const TASK_BADGE_STYLES: Record<string, string> = {
	Liner: 'bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-200',
	Picker: 'bg-pink-100 text-pink-700 dark:bg-pink-900 dark:text-pink-200',
	Dropper: 'bg-sky-100 text-sky-700 dark:bg-sky-900 dark:text-sky-200',
	Feeder: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-200',
	Whole: 'bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white',
};

const getTaskBadgeStyle = (taskType: string): string => {
	return (
		TASK_BADGE_STYLES[taskType] ||
		'bg-blue-500/10 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400'
	);
};

const formatCurrency = (amount: number): string => {
	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(amount);
};

const formatDateTime = (dateTimeStr: string) => {
	try {
		const date = new Date(dateTimeStr);
		const dateFormatted = date.toLocaleDateString('en-US', {
			weekday: 'short',
			month: 'short',
			day: 'numeric',
			year: 'numeric',
		});
		const hours = date.getHours();
		const minutes = date.getMinutes();
		const period = hours >= 12 ? 'PM' : 'AM';
		const displayHours = hours % 12 || 12;
		const time = `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
		return { date: dateFormatted, time };
	} catch {
		return { date: '-', time: '-' };
	}
};

const formatDate = (dateTimeStr: string): string => {
	try {
		const date = new Date(dateTimeStr);
		return date.toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
		});
	} catch {
		return '-';
	}
};

interface BookingDetailModalProps {
	bookingId: number | null;
	onClose: () => void;
}

const BookingDetailModal = ({ bookingId, onClose }: BookingDetailModalProps) => {
	const navigate = useNavigate();
	const [detail, setDetail] = useState<StaffBookingDetail | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!bookingId) {
			// Don't clear detail here — preserves content during close animation
			setError(null);
			return;
		}

		// Clear previous booking's data before fetching new one
		setDetail(null);
		setIsLoading(true);
		setError(null);

		staffProfileApi
			.getBookingDetail(bookingId)
			.then(data => setDetail(data))
			.catch(err => {
				const apiError = err as ApiError;
				setError(apiError.message || 'Failed to load booking details');
			})
			.finally(() => setIsLoading(false));
	}, [bookingId]);

	const handleRetry = () => {
		if (bookingId) {
			setError(null);
			setIsLoading(true);
			staffProfileApi
				.getBookingDetail(bookingId)
				.then(data => setDetail(data))
				.catch(err => {
					const apiError = err as ApiError;
					setError(apiError.message || 'Failed to load booking details');
				})
				.finally(() => setIsLoading(false));
		}
	};

	const headerContent = detail ? (
		<div
			className="px-6 py-5"
			style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
		>
			<div className="pr-10">
				<h2 className="text-xl font-bold text-white mb-1">{detail.serviceName}</h2>
				<p className="text-white/80 text-sm">
					{[detail.size, detail.hairLength].filter(Boolean).join(' \u2022 ')}
				</p>
			</div>
		</div>
	) : undefined;

	return (
		<Modal
			isOpen={bookingId !== null}
			onClose={onClose}
			size="xl"
			headerContent={headerContent}
			panelClassName="max-h-[90vh] flex flex-col"
			bodyClassName="!p-0 flex-1 flex flex-col min-h-0"
		>
			{/* Loading State */}
			{isLoading && (
				<div className="flex items-center justify-center min-h-[300px]">
					<Spinner size="lg" className="text-[#667eea]" />
				</div>
			)}

			{/* Error State */}
			{error && !isLoading && (
				<div className="px-4 py-4 sm:px-6 sm:py-5">
					<Alert
						type="warning"
						icon={<ExclamationTriangleIcon className="w-8 h-8" />}
						title="Unable to Load Details"
						message={error}
						actions={[{ label: 'Retry', onClick: handleRetry, primary: true }]}
						onDismiss={() => setError(null)}
					/>
				</div>
			)}

			{/* Content */}
			{detail && !isLoading && !error && (
				<>
					{/* Scrollable content area */}
					<div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">
						{/* Quick Info Row */}
						<div className="grid grid-cols-3 gap-3 mb-5">
							<div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 sm:p-3.5">
								<div className="flex items-center gap-1.5 mb-1.5">
									<CalendarDaysIcon className="w-4 h-4 text-[#667eea]" />
									<span className="text-xs text-gray-500 dark:text-gray-400">
										Date
									</span>
								</div>
								<div className="text-sm font-semibold text-gray-900 dark:text-white">
									{formatDateTime(detail.appointmentDateTime).date}
								</div>
							</div>
							<div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 sm:p-3.5">
								<div className="flex items-center gap-1.5 mb-1.5">
									<ClockIcon className="w-4 h-4 text-[#667eea]" />
									<span className="text-xs text-gray-500 dark:text-gray-400">
										Time
									</span>
								</div>
								<div className="text-sm font-semibold text-gray-900 dark:text-white">
									{formatDateTime(detail.appointmentDateTime).time}
								</div>
							</div>
							<div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 sm:p-3.5">
								<div className="flex items-center gap-1.5 mb-1.5">
									<CurrencyDollarIcon className="w-4 h-4 text-emerald-500" />
									<span className="text-xs text-gray-500 dark:text-gray-400">
										Price
									</span>
								</div>
								<div className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
									{formatCurrency(detail.amount)}
								</div>
							</div>
						</div>

						{/* Two-column grid */}
						<div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
							{/* Left Column - Client Information + Task Details */}
							<div className="space-y-5">
								{/* Client Information */}
								<div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-4 sm:p-5">
									<h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
										Client Information
									</h3>

									{/* Client avatar + name */}
									<div className="flex items-center gap-3 mb-4">
										<div
											className="w-11 h-11 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
											style={{
												background:
													'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
											}}
										>
											{detail.customerInitials || '?'}
										</div>
										<div>
											<div className="text-sm font-semibold text-gray-900 dark:text-white">
												{detail.customerName}
											</div>
											<div className="text-xs text-gray-500 dark:text-gray-400">
												Client since {formatDate(detail.clientSince)} &bull;{' '}
												{detail.visitCount} visits
											</div>
										</div>
									</div>

									{/* Total spent */}
									<div className="flex items-center justify-between py-2.5 border-t border-gray-200 dark:border-gray-600">
										<span className="text-xs text-gray-500 dark:text-gray-400">
											Total Spent
										</span>
										<span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
											{formatCurrency(detail.totalSpent)}
										</span>
									</div>

									{/* Notes */}
									{detail.notes && (
										<div className="mt-3 bg-white dark:bg-gray-800 border-l-3 border-[#667eea] rounded-lg p-3.5">
											<div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
												Notes
											</div>
											<p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
												{detail.notes}
											</p>
										</div>
									)}
								</div>

								{/* Task Details */}
								{detail.selectedTasks && detail.selectedTasks.length > 0 && (
									<div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-4 sm:p-5">
										<h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
											Task Details
										</h3>

										{/* Task badges */}
										<div className="flex flex-wrap gap-1.5 mb-4">
											{detail.selectedTasks.map(task => (
												<span
													key={task.serviceTaskId}
													className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-semibold ${getTaskBadgeStyle(task.taskType)}`}
												>
													{task.taskType}
												</span>
											))}
										</div>

										{/* Task summary grid */}
										<div className="grid grid-cols-3 gap-3">
											<div>
												<div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">
													Total Tasks
												</div>
												<div className="text-sm font-semibold text-gray-900 dark:text-white">
													{detail.totalTasks}
												</div>
											</div>
											<div>
												<div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">
													Staff Count
												</div>
												<div className="text-sm font-semibold text-gray-900 dark:text-white">
													{detail.staffCount}
												</div>
											</div>
										</div>
									</div>
								)}
							</div>

							{/* Right Column - Service & Assignment + Team */}
							<div className="space-y-5">
								{/* Service & Assignment */}
								<div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-4 sm:p-5">
									<h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
										Service & Assignment
									</h3>

									{/* Service details grid */}
									<div className="grid grid-cols-2 gap-3 mb-4">
										<div>
											<div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">
												Service Type
											</div>
											<div className="text-sm font-medium text-gray-900 dark:text-white">
												{detail.serviceName}
											</div>
										</div>
										<div>
											<div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">
												Size
											</div>
											<div className="text-sm font-medium text-gray-900 dark:text-white">
												{detail.size || '-'}
											</div>
										</div>
										<div>
											<div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">
												Length
											</div>
											<div className="text-sm font-medium text-gray-900 dark:text-white">
												{detail.hairLength || '-'}
											</div>
										</div>
										<div>
											<div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">
												Service Price
											</div>
											<div className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
												{formatCurrency(detail.amount)}
											</div>
										</div>
									</div>

									{/* Divider */}
									<div className="border-t border-gray-200 dark:border-gray-600 my-4" />

									{/* Your Assignment */}
									<h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
										Your Assignment
									</h4>
									<div className="grid grid-cols-3 gap-3">
										<div>
											<div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">
												Your Task
											</div>
											<span
												className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-semibold ${getTaskBadgeStyle(detail.yourTask)}`}
											>
												{detail.yourTask || '-'}
											</span>
										</div>
										<div>
											<div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">
												Task Price
											</div>
											<div className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
												{formatCurrency(detail.taskPrice)}
											</div>
										</div>
										<div>
											<div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">
												Duration
											</div>
											<div className="text-sm font-medium text-gray-900 dark:text-white">
												{detail.taskDuration} min
											</div>
										</div>
									</div>
								</div>

								{/* Team */}
								{detail.assignedStaff && detail.assignedStaff.length > 0 && (
									<div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-4 sm:p-5">
										<div className="flex items-center gap-2 mb-4">
											<UserGroupIcon className="w-4 h-4 text-[#667eea]" />
											<h3 className="text-sm font-semibold text-gray-900 dark:text-white">
												Team
											</h3>
										</div>
										<div className="flex flex-wrap gap-2">
											{detail.assignedStaff.map(staff => (
												<div
													key={staff.id}
													className="flex items-center gap-2 bg-white dark:bg-gray-800 rounded-lg px-3 py-2 shadow-sm"
												>
													<div
														className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0"
														style={{
															background:
																'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
														}}
													>
														{staff.initials}
													</div>
													<div>
														<div className="text-xs font-medium text-gray-900 dark:text-white">
															{staff.name}
														</div>
														<span
															className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${getTaskBadgeStyle(staff.taskType)}`}
														>
															{staff.taskType}
														</span>
													</div>
												</div>
											))}
										</div>
									</div>
								)}
							</div>
						</div>
					</div>

					{/* Sticky Footer - Submit TCR (hidden for completed/cancelled/upcoming) */}
					{detail.status !== 'COMPLETED' && detail.status !== 'CANCELLED' && new Date(detail.appointmentDateTime) <= new Date() && (
						<div className="shrink-0 px-4 py-4 sm:px-6 border-t border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 rounded-b-2xl flex justify-end">
							<button
								onClick={e => {
									e.stopPropagation();
									onClose();
									navigate(`/staff/tcr/new?appointmentId=${detail.id}`);
								}}
								className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-lg"
								style={{
									background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
								}}
							>
								<DocumentTextIcon className="w-4.5 h-4.5" />
								Submit TCR
							</button>
						</div>
					)}
				</>
			)}
		</Modal>
	);
};

export default BookingDetailModal;
